import { useMemo, useState } from "react";
import {
  ChevronLeft,
  ChevronRight,
  Mail,
  Phone,
  Tag,
  FileText,
  Clock,
  X,
  Check,
  UserX,
  CheckCircle2,
  Loader2,
} from "lucide-react";

// Status -> color
const STATUS_COLORS = {
  confirmed: { dot: "#2563EB", text: "#60A5FA", border: "border-[#2563EB]/60" },
  pending: { dot: "#A1A1AA", text: "#A1A1AA", border: "border-white/20" },
  pending_payment: { dot: "#A1A1AA", text: "#A1A1AA", border: "border-white/20" },
  completed: { dot: "#22c55e", text: "#86efac", border: "border-green-500/40" },
  cancelled: { dot: "#52525b", text: "#71717a", border: "border-white/10" },
  expired: { dot: "#52525b", text: "#71717a", border: "border-white/10" },
};

const MONTHS = [
  "January","February","March","April","May","June",
  "July","August","September","October","November","December",
];
const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const fmtDate = (y, m, d) =>
  `${y}-${String(m + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;

const todayStr = () => {
  const t = new Date();
  return fmtDate(t.getFullYear(), t.getMonth(), t.getDate());
};

const slotOrder = (s) => {
  // "12:00 PM", "1:00 PM" ... convert to minutes for sorting
  const m = /^(\d{1,2}):(\d{2})\s*(AM|PM)$/i.exec(s);
  if (!m) return 9999;
  let h = parseInt(m[1], 10) % 12;
  if (m[3].toUpperCase() === "PM") h += 12;
  return h * 60 + parseInt(m[2], 10);
};

export default function AdminCalendar({ bookings = [], onStatusChange = null }) {
  const today = new Date();
  const [view, setView] = useState({ y: today.getFullYear(), m: today.getMonth() });
  const [activeDay, setActiveDay] = useState(todayStr());
  const [statusFilter, setStatusFilter] = useState("all"); // all | confirmed | pending_payment
  const [pendingId, setPendingId] = useState(null); // booking id being updated

  const handleStatus = async (booking, status) => {
    if (!onStatusChange) return;
    setPendingId(booking.id);
    try {
      await onStatusChange(booking.id, status);
    } finally {
      setPendingId(null);
    }
  };

  // group bookings by date for fast lookups
  const byDate = useMemo(() => {
    const map = new Map();
    for (const b of bookings) {
      if (statusFilter !== "all" && b.status !== statusFilter) continue;
      const list = map.get(b.date) || [];
      list.push(b);
      map.set(b.date, list);
    }
    // sort each day by slot time
    for (const [k, list] of map.entries()) {
      list.sort((a, b) => slotOrder(a.time_slot) - slotOrder(b.time_slot));
      map.set(k, list);
    }
    return map;
  }, [bookings, statusFilter]);

  // build the day grid (Monday-first)
  const grid = useMemo(() => {
    const { y, m } = view;
    const firstOfMonth = new Date(y, m, 1);
    const startWeekday = (firstOfMonth.getDay() + 6) % 7; // 0 = Monday
    const daysInMonth = new Date(y, m + 1, 0).getDate();
    const prevDays = new Date(y, m, 0).getDate();
    const cells = [];

    for (let i = startWeekday - 1; i >= 0; i--) {
      const d = prevDays - i;
      cells.push({ y, m: m - 1, d, otherMonth: true });
    }
    for (let d = 1; d <= daysInMonth; d++) {
      cells.push({ y, m, d, otherMonth: false });
    }
    while (cells.length % 7 !== 0 || cells.length < 42) {
      const last = cells[cells.length - 1];
      const next = new Date(last.y, last.m, last.d + 1);
      cells.push({
        y: next.getFullYear(),
        m: next.getMonth(),
        d: next.getDate(),
        otherMonth: true,
      });
      if (cells.length >= 42) break;
    }
    return cells;
  }, [view]);

  const goPrev = () => {
    const d = new Date(view.y, view.m - 1, 1);
    setView({ y: d.getFullYear(), m: d.getMonth() });
  };
  const goNext = () => {
    const d = new Date(view.y, view.m + 1, 1);
    setView({ y: d.getFullYear(), m: d.getMonth() });
  };
  const goToday = () => {
    const t = new Date();
    setView({ y: t.getFullYear(), m: t.getMonth() });
    setActiveDay(todayStr());
  };

  const activeBookings = byDate.get(activeDay) || [];
  const todaysSessions = useMemo(() => {
    const list = (byDate.get(todayStr()) || []).filter(
      (b) => !["cancelled", "expired"].includes(b.status)
    );
    return list;
  }, [byDate]);

  const totalThisMonth = useMemo(() => {
    let n = 0;
    for (const [k, v] of byDate.entries()) {
      const [yy, mm] = k.split("-").map(Number);
      if (yy === view.y && mm - 1 === view.m) n += v.length;
    }
    return n;
  }, [byDate, view]);

  return (
    <div data-testid="admin-calendar" className="mt-8 space-y-6">
      {/* Today's Schedule strip */}
      <div data-testid="todays-schedule" className="bg-[#070B14] border border-white/5">
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/5">
          <div className="flex items-center gap-3">
            <div className="text-[10px] tracking-[0.3em] uppercase text-[#60A5FA]">
              [ Today's Schedule ]
            </div>
            <div className="font-display text-lg font-semibold uppercase tracking-tight">
              {new Date().toLocaleDateString(undefined, { weekday: "long", month: "short", day: "numeric" })}
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-right">
              <div className="font-display text-2xl font-bold text-[#60A5FA] leading-none">
                {todaysSessions.length}
              </div>
              <div className="text-[10px] tracking-[0.25em] uppercase text-white/50 mt-1">
                {todaysSessions.length === 1 ? "session" : "sessions"}
              </div>
            </div>
            <button
              onClick={() => {
                const t = new Date();
                setView({ y: t.getFullYear(), m: t.getMonth() });
                setActiveDay(todayStr());
              }}
              data-testid="todays-jump-btn"
              className="text-[10px] tracking-[0.25em] uppercase border border-white/15 hover:border-[#2563EB] hover:text-[#60A5FA] px-3 py-1.5 text-white"
            >
              View Day
            </button>
          </div>
        </div>

        {todaysSessions.length === 0 ? (
          <div className="px-5 py-10 text-center text-[#A1A1AA] text-sm">
            No sessions on the floor today. Take the day or jump in the lab.
          </div>
        ) : (
          <div className="divide-y divide-white/5">
            {todaysSessions.map((b) => {
              const c = STATUS_COLORS[b.status] || STATUS_COLORS.pending;
              const isCompleted = b.status === "completed";
              const isUpdating = pendingId === b.id;
              return (
                <div
                  key={b.id}
                  data-testid={`today-row-${b.id}`}
                  className={`px-5 py-4 grid grid-cols-12 gap-4 items-center ${
                    isCompleted ? "opacity-60" : ""
                  }`}
                >
                  <div className="col-span-12 sm:col-span-2 flex items-center gap-2">
                    <span
                      className="w-1.5 h-1.5 rounded-full"
                      style={{ background: c.dot }}
                    />
                    <span className={`font-display text-xl font-bold tracking-tight ${isCompleted ? "line-through" : ""}`}>
                      {b.time_slot}
                    </span>
                  </div>
                  <div className="col-span-12 sm:col-span-4">
                    <div className="font-medium text-white">{b.name}</div>
                    <div className="text-[10px] tracking-widest uppercase text-white/40 mt-0.5">
                      {b.program}
                      {b.pack_record_id && (
                        <span className="ml-2 text-[#60A5FA]">· Pack</span>
                      )}
                    </div>
                  </div>
                  <div className="col-span-12 sm:col-span-3 flex flex-col gap-1 text-xs">
                    <a
                      href={`tel:${b.phone}`}
                      className="text-[#A1A1AA] hover:text-white flex items-center gap-1.5"
                    >
                      <Phone size={11} className="text-[#60A5FA]" />
                      {b.phone}
                    </a>
                    <a
                      href={`mailto:${b.email}`}
                      className="text-[#A1A1AA] hover:text-white flex items-center gap-1.5 truncate"
                    >
                      <Mail size={11} className="text-[#60A5FA]" />
                      <span className="truncate">{b.email}</span>
                    </a>
                  </div>
                  <div className="col-span-12 sm:col-span-3 flex items-center justify-end gap-2 flex-wrap">
                    {isCompleted ? (
                      <span className="inline-flex items-center gap-1 text-[10px] tracking-[0.25em] uppercase text-green-400 border border-green-500/40 px-3 py-2">
                        <CheckCircle2 size={12} /> Completed
                      </span>
                    ) : (
                      <>
                        <button
                          disabled={isUpdating}
                          onClick={() => handleStatus(b, "completed")}
                          data-testid={`today-complete-${b.id}`}
                          className="inline-flex items-center gap-1 text-[10px] tracking-[0.25em] uppercase bg-[#2563EB] hover:bg-[#1D4ED8] disabled:opacity-60 text-white px-3 py-2 transition-colors"
                        >
                          {isUpdating ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />}
                          Complete
                        </button>
                        <button
                          disabled={isUpdating}
                          onClick={() => handleStatus(b, "cancelled")}
                          data-testid={`today-noshow-${b.id}`}
                          className="inline-flex items-center gap-1 text-[10px] tracking-[0.25em] uppercase border border-white/15 hover:border-red-500/50 hover:text-red-400 text-white/70 px-3 py-2 transition-colors"
                          title="Mark as no-show / cancel"
                        >
                          <UserX size={12} /> No-Show
                        </button>
                      </>
                    )}
                  </div>
                  {b.notes && (
                    <div className="col-span-12 text-xs text-white/60 flex items-start gap-2 pt-1">
                      <FileText size={11} className="text-[#60A5FA] mt-0.5 flex-shrink-0" />
                      {b.notes}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
      {/* Calendar */}
      <div className="lg:col-span-3 bg-[#070B14] border border-white/5">
        <div className="flex items-center justify-between p-5 border-b border-white/5">
          <div className="flex items-center gap-3">
            <button
              onClick={goPrev}
              data-testid="calendar-prev"
              className="p-2 border border-white/10 hover:border-[#2563EB] text-white/80"
            >
              <ChevronLeft size={16} />
            </button>
            <button
              onClick={goNext}
              data-testid="calendar-next"
              className="p-2 border border-white/10 hover:border-[#2563EB] text-white/80"
            >
              <ChevronRight size={16} />
            </button>
            <button
              onClick={goToday}
              data-testid="calendar-today"
              className="ml-2 text-[10px] tracking-[0.25em] uppercase border border-white/15 hover:border-[#2563EB] hover:text-[#60A5FA] px-3 py-1.5 text-white"
            >
              Today
            </button>
          </div>
          <div className="font-display text-xl md:text-2xl font-bold uppercase tracking-tight">
            {MONTHS[view.m]} <span className="text-[#60A5FA]">{view.y}</span>
          </div>
          <div className="hidden md:block text-[10px] tracking-[0.25em] uppercase text-white/40">
            {totalThisMonth} sessions
          </div>
        </div>

        <div className="px-5 pt-4 flex items-center gap-2 flex-wrap">
          {[
            { k: "all", label: "All" },
            { k: "confirmed", label: "Confirmed" },
            { k: "pending_payment", label: "Pending" },
            { k: "cancelled", label: "Cancelled" },
          ].map((f) => (
            <button
              key={f.k}
              onClick={() => setStatusFilter(f.k)}
              data-testid={`calendar-filter-${f.k}`}
              className={`text-[10px] tracking-[0.25em] uppercase px-3 py-1.5 border transition-colors ${
                statusFilter === f.k
                  ? "bg-[#2563EB] border-[#2563EB] text-white"
                  : "border-white/10 text-white/60 hover:border-[#2563EB] hover:text-[#60A5FA]"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-7 px-5 mt-3">
          {DAYS.map((d) => (
            <div
              key={d}
              className="py-3 text-[10px] tracking-[0.3em] uppercase text-white/40 text-center"
            >
              {d}
            </div>
          ))}
        </div>

        <div className="grid grid-cols-7 px-5 pb-5 gap-px bg-white/5 border-t border-white/5">
          {grid.map((cell, idx) => {
            const dateStr = fmtDate(cell.y, cell.m, cell.d);
            const items = byDate.get(dateStr) || [];
            const isToday = dateStr === todayStr();
            const isActive = dateStr === activeDay;
            const isWeekend = (() => {
              const w = new Date(cell.y, cell.m, cell.d).getDay();
              return w === 0 || w === 6;
            })();

            return (
              <button
                key={idx}
                onClick={() => setActiveDay(dateStr)}
                data-testid={`calendar-day-${dateStr}`}
                className={`relative aspect-square sm:aspect-[4/3] p-2 text-left transition-colors ${
                  cell.otherMonth
                    ? "bg-[#050505]/60 text-white/25"
                    : isActive
                    ? "bg-[#0a1020] ring-1 ring-[#2563EB]"
                    : "bg-[#050505] hover:bg-[#0a1020]"
                } ${isWeekend && !cell.otherMonth ? "opacity-70" : ""}`}
              >
                <div className="flex items-center justify-between">
                  <span
                    className={`text-xs font-semibold ${
                      isToday ? "text-[#60A5FA]" : "text-white/85"
                    }`}
                  >
                    {cell.d}
                  </span>
                  {isToday && (
                    <span className="text-[8px] tracking-widest uppercase text-[#60A5FA]">
                      Today
                    </span>
                  )}
                </div>

                {items.length > 0 && (
                  <>
                    <div className="mt-1 hidden sm:flex flex-col gap-0.5">
                      {items.slice(0, 3).map((b) => {
                        const c = STATUS_COLORS[b.status] || STATUS_COLORS.pending;
                        return (
                          <div
                            key={b.id}
                            className="flex items-center gap-1 text-[10px] truncate"
                            style={{ color: c.text }}
                          >
                            <span
                              className="w-1.5 h-1.5 rounded-full flex-shrink-0"
                              style={{ background: c.dot }}
                            />
                            <span className="truncate">
                              {b.time_slot} · {b.name?.split(" ")[0] || "—"}
                            </span>
                          </div>
                        );
                      })}
                      {items.length > 3 && (
                        <div className="text-[10px] text-white/40">
                          +{items.length - 3} more
                        </div>
                      )}
                    </div>

                    <div className="sm:hidden absolute top-1 right-1 w-5 h-5 bg-[#2563EB] text-white text-[10px] font-bold flex items-center justify-center rounded-full">
                      {items.length}
                    </div>
                  </>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Day detail panel */}
      <div className="lg:col-span-2 bg-[#070B14] border border-white/5">
        <div className="p-5 border-b border-white/5 flex items-center justify-between">
          <div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-[#60A5FA]">
              [ Day Details ]
            </div>
            <div className="font-display text-xl font-bold uppercase tracking-tight mt-1">
              {activeDay}
            </div>
          </div>
          <div className="text-right">
            <div className="font-display text-3xl font-bold text-[#60A5FA]">
              {activeBookings.length}
            </div>
            <div className="text-[10px] tracking-[0.25em] uppercase text-white/50">
              {activeBookings.length === 1 ? "session" : "sessions"}
            </div>
          </div>
        </div>

        <div className="p-5">
          {activeBookings.length === 0 ? (
            <div data-testid="calendar-empty-day" className="py-10 text-center text-[#A1A1AA] text-sm">
              No sessions on this day.
            </div>
          ) : (
            <div className="space-y-3">
              {activeBookings.map((b) => {
                const c = STATUS_COLORS[b.status] || STATUS_COLORS.pending;
                return (
                  <div
                    key={b.id}
                    data-testid={`calendar-booking-${b.id}`}
                    className={`bg-[#050505] border ${c.border} p-4`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-center gap-2">
                        <span
                          className="w-1.5 h-1.5 rounded-full"
                          style={{ background: c.dot }}
                        />
                        <span
                          className="font-display text-lg font-bold tracking-tight"
                          style={{ color: "#fff" }}
                        >
                          {b.time_slot}
                        </span>
                      </div>
                      <span
                        className="text-[10px] tracking-[0.25em] uppercase px-2 py-1 border"
                        style={{
                          color: c.text,
                          borderColor: "rgba(255,255,255,0.1)",
                        }}
                      >
                        {b.status.replace("_", " ")}
                      </span>
                    </div>
                    <div className="mt-3 text-white font-medium">{b.name}</div>
                    <div className="mt-3 space-y-1.5 text-xs">
                      <div className="flex items-center gap-2 text-[#A1A1AA]">
                        <Mail size={12} className="text-[#60A5FA]" />
                        <a
                          href={`mailto:${b.email}`}
                          className="hover:text-white truncate"
                        >
                          {b.email}
                        </a>
                      </div>
                      <div className="flex items-center gap-2 text-[#A1A1AA]">
                        <Phone size={12} className="text-[#60A5FA]" />
                        <a href={`tel:${b.phone}`} className="hover:text-white">
                          {b.phone}
                        </a>
                      </div>
                      <div className="flex items-center gap-2 text-[#A1A1AA]">
                        <Tag size={12} className="text-[#60A5FA]" />
                        <span>{b.program}</span>
                        {b.pack_record_id ? (
                          <span className="ml-2 text-[9px] tracking-widest uppercase text-[#60A5FA] border border-[#60A5FA]/30 px-1.5 py-0.5">
                            Pack
                          </span>
                        ) : b.amount > 0 ? (
                          <span className="ml-2 text-[9px] tracking-widest uppercase text-white/50">
                            ${b.amount?.toFixed(0)}
                          </span>
                        ) : null}
                      </div>
                      {b.notes && (
                        <div className="flex items-start gap-2 text-[#A1A1AA] pt-1">
                          <FileText size={12} className="text-[#60A5FA] mt-0.5" />
                          <span className="text-white/80">{b.notes}</span>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
      </div>
    </div>
  );
}
